import { createNoise2D, createNoise3D } from 'simplex-noise';
import alea from 'alea';
import { CHUNK_SIZE } from '../constants.js';
import { BlockId } from './BlockType.js';

// End dimension constants
const END_HEIGHT = 128;
const MAIN_ISLAND_RADIUS_X = 60;
const MAIN_ISLAND_RADIUS_Z = 60;
const MAIN_ISLAND_RADIUS_Y = 20;
const MAIN_ISLAND_CENTER_Y = 48;
const OUTER_ISLAND_MIN_DIST = 200;
const OUTER_ISLAND_GRID = 100;    // Espaciado entre islas exteriores
const OUTER_ISLAND_MIN_RADIUS = 8;
const OUTER_ISLAND_MAX_RADIUS = 15;
const PILLAR_RING_RADIUS = 40;
const NUM_PILLARS = 10;

export class EndGenerator {
  constructor(seed) {
    this.noise2D = createNoise2D(alea(seed + '-end-2d'));
    this.noise3D = createNoise3D(alea(seed + '-end-3d'));
    this.islandNoise = createNoise3D(alea(seed + '-end-island'));
    this.islandNoise2 = createNoise2D(alea(seed + '-end-island2'));
    this.detailNoise = createNoise2D(alea(seed + '-end-detail'));
    this.chorusNoise = createNoise2D(alea(seed + '-end-chorus'));
    this._heightCache = new Map();
    this._pillarPositions = null;
  }

  fillChunk(chunk) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const wx = wx0 + lx;
        const wz = wz0 + lz;

        for (let ly = 0; ly < CHUNK_SIZE; ly++) {
          const wy = wy0 + ly;
          const blockId = this._getBlockAt(wx, wy, wz);
          if (blockId !== BlockId.AIR) {
            chunk.setBlock(lx, ly, lz, blockId);
          }
        }
      }
    }

    // Structure pass: obsidian pillars + chorus plants
    this._generatePillars(chunk);
    this._generateChorusPlants(chunk);
    chunk.isDirty = true;
  }

  getHeight(wx, wz) {
    // Isla principal: calcular superficie del elipsoide
    const dist2D = Math.sqrt(wx * wx + wz * wz);
    if (dist2D < MAIN_ISLAND_RADIUS_X) {
      const t = dist2D / MAIN_ISLAND_RADIUS_X;
      const baseH = MAIN_ISLAND_CENTER_Y + Math.floor(MAIN_ISLAND_RADIUS_Y * Math.sqrt(Math.max(0, 1 - t * t)));
      const nv = this.noise2D(wx * 0.04, wz * 0.04) * 3;
      return Math.floor(baseH + nv);
    }

    // Islas exteriores
    const outerH = this._getOuterIslandHeight(wx, wz);
    if (outerH > 0) return outerH;

    return 0; // Void
  }

  // Encontrar Y seguro para spawn
  findSpawnY(wx, wz) {
    for (let y = MAIN_ISLAND_CENTER_Y; y < END_HEIGHT - 10; y++) {
      const block = this._getBlockAt(wx, y, wz);
      const above1 = this._getBlockAt(wx, y + 1, wz);
      const above2 = this._getBlockAt(wx, y + 2, wz);
      if (block !== BlockId.AIR &&
          above1 === BlockId.AIR && above2 === BlockId.AIR) {
        return y + 1;
      }
    }
    // Fallback: centro de isla principal
    return MAIN_ISLAND_CENTER_Y + MAIN_ISLAND_RADIUS_Y + 2;
  }

  _getBlockAt(wx, wy, wz) {
    // Capa de bedrock de seguridad en Y=0
    if (wy <= 0) return BlockId.BEDROCK;

    // Fuera de rango vertical = aire (void)
    if (wy >= END_HEIGHT) return BlockId.AIR;

    // Intentar isla principal
    const mainBlock = this._getMainIslandBlock(wx, wy, wz);
    if (mainBlock !== BlockId.AIR) return mainBlock;

    // Intentar islas exteriores
    const outerBlock = this._getOuterIslandBlock(wx, wy, wz);
    if (outerBlock !== BlockId.AIR) return outerBlock;

    // Todo lo demas es void (AIR)
    return BlockId.AIR;
  }

  // --- Isla Principal ---
  // Elipsoide centrado en (0, MAIN_ISLAND_CENTER_Y, 0) con bordes irregulares via noise
  _getMainIslandBlock(wx, wy, wz) {
    // Distancia normalizada al centro del elipsoide
    const nx = wx / MAIN_ISLAND_RADIUS_X;
    const ny = (wy - MAIN_ISLAND_CENTER_Y) / MAIN_ISLAND_RADIUS_Y;
    const nz = wz / MAIN_ISLAND_RADIUS_Z;

    // Ecuacion del elipsoide: nx^2 + ny^2 + nz^2 <= 1
    let dist2 = nx * nx + ny * ny + nz * nz;

    // Modular bordes con noise 3D para forma irregular
    const edgeNoise = this.noise3D(wx * 0.05, wy * 0.08, wz * 0.05) * 0.25;
    const detailNoise = this.noise3D(wx * 0.12, wy * 0.15, wz * 0.12) * 0.08;
    dist2 -= (edgeNoise + detailNoise);

    if (dist2 > 1.0) return BlockId.AIR;

    // Superficie superior: end stone con variaciones
    if (dist2 > 0.85) {
      // Capa superficial mas fina
      const surfNoise = this.noise2D(wx * 0.1, wz * 0.1);
      if (surfNoise > 0.7 && wy > MAIN_ISLAND_CENTER_Y) {
        return BlockId.END_STONE_BRICKS;
      }
      return BlockId.END_STONE;
    }

    return BlockId.END_STONE;
  }

  // --- Islas Exteriores ---
  // Aparecen a distancia > OUTER_ISLAND_MIN_DIST, en una grilla con ruido
  _getOuterIslandBlock(wx, wy, wz) {
    const dist2D = Math.sqrt(wx * wx + wz * wz);
    if (dist2D < OUTER_ISLAND_MIN_DIST) return BlockId.AIR;

    // Determinar celda de la grilla
    const gx = Math.floor(wx / OUTER_ISLAND_GRID);
    const gz = Math.floor(wz / OUTER_ISLAND_GRID);

    // Revisar esta celda y las adyacentes
    for (let dgx = -1; dgx <= 1; dgx++) {
      for (let dgz = -1; dgz <= 1; dgz++) {
        const cgx = gx + dgx;
        const cgz = gz + dgz;

        // Determinar si esta celda tiene isla (deterministico)
        const cellChance = this.islandNoise2(cgx * 1.7, cgz * 1.7);
        if (cellChance < 0.15) continue; // ~42% de celdas tienen isla

        // Posicion del centro de la isla dentro de la celda
        const icx = cgx * OUTER_ISLAND_GRID +
          Math.floor((this.islandNoise2(cgx * 0.7 + 300, cgz * 0.7 + 300) + 1) * OUTER_ISLAND_GRID * 0.3);
        const icz = cgz * OUTER_ISLAND_GRID +
          Math.floor((this.islandNoise2(cgx * 0.5 + 600, cgz * 0.5 + 600) + 1) * OUTER_ISLAND_GRID * 0.3);

        // Verificar que el centro esta lejos del origen
        const centerDist = Math.sqrt(icx * icx + icz * icz);
        if (centerDist < OUTER_ISLAND_MIN_DIST) continue;

        // Altura de la isla (40-70)
        const icy = 40 + Math.floor((this.islandNoise2(cgx * 2.1, cgz * 2.1) + 1) * 15);

        // Radio de la isla (8-15)
        const radius = OUTER_ISLAND_MIN_RADIUS +
          Math.floor((this.islandNoise2(cgx * 3.3 + 100, cgz * 3.3 + 100) + 1) *
          (OUTER_ISLAND_MAX_RADIUS - OUTER_ISLAND_MIN_RADIUS) * 0.5);
        const radiusY = Math.floor(radius * 0.5); // Mas achatada verticalmente

        // Distancia normalizada
        const dnx = (wx - icx) / radius;
        const dny = (wy - icy) / radiusY;
        const dnz = (wz - icz) / radius;
        let d2 = dnx * dnx + dny * dny + dnz * dnz;

        // Bordes irregulares
        const en = this.islandNoise(wx * 0.08, wy * 0.12, wz * 0.08) * 0.2;
        d2 -= en;

        if (d2 <= 1.0) {
          // Detalles superficiales
          if (d2 > 0.8) {
            const sv = this.detailNoise(wx * 0.15, wz * 0.15);
            if (sv > 0.6) return BlockId.END_STONE_BRICKS;
          }
          return BlockId.END_STONE;
        }
      }
    }

    return BlockId.AIR;
  }

  // Obtener altura de isla exterior en posicion dada (para getHeight)
  _getOuterIslandHeight(wx, wz) {
    const dist2D = Math.sqrt(wx * wx + wz * wz);
    if (dist2D < OUTER_ISLAND_MIN_DIST) return 0;

    const gx = Math.floor(wx / OUTER_ISLAND_GRID);
    const gz = Math.floor(wz / OUTER_ISLAND_GRID);

    for (let dgx = -1; dgx <= 1; dgx++) {
      for (let dgz = -1; dgz <= 1; dgz++) {
        const cgx = gx + dgx;
        const cgz = gz + dgz;

        const cellChance = this.islandNoise2(cgx * 1.7, cgz * 1.7);
        if (cellChance < 0.15) continue;

        const icx = cgx * OUTER_ISLAND_GRID +
          Math.floor((this.islandNoise2(cgx * 0.7 + 300, cgz * 0.7 + 300) + 1) * OUTER_ISLAND_GRID * 0.3);
        const icz = cgz * OUTER_ISLAND_GRID +
          Math.floor((this.islandNoise2(cgx * 0.5 + 600, cgz * 0.5 + 600) + 1) * OUTER_ISLAND_GRID * 0.3);

        const centerDist = Math.sqrt(icx * icx + icz * icz);
        if (centerDist < OUTER_ISLAND_MIN_DIST) continue;

        const icy = 40 + Math.floor((this.islandNoise2(cgx * 2.1, cgz * 2.1) + 1) * 15);
        const radius = OUTER_ISLAND_MIN_RADIUS +
          Math.floor((this.islandNoise2(cgx * 3.3 + 100, cgz * 3.3 + 100) + 1) *
          (OUTER_ISLAND_MAX_RADIUS - OUTER_ISLAND_MIN_RADIUS) * 0.5);

        const dx = wx - icx;
        const dz = wz - icz;
        const hDist = Math.sqrt(dx * dx + dz * dz);

        if (hDist < radius) {
          // Superficie de la isla
          const t = hDist / radius;
          const radiusY = Math.floor(radius * 0.5);
          return icy + Math.floor(radiusY * Math.sqrt(Math.max(0, 1 - t * t)));
        }
      }
    }

    return 0;
  }

  // --- Pilares de Obsidiana ---
  // 10 pilares en un anillo de radio 40 alrededor del centro
  _getPillarPositions() {
    if (this._pillarPositions) return this._pillarPositions;

    const pillars = [];
    for (let i = 0; i < NUM_PILLARS; i++) {
      const angle = (i / NUM_PILLARS) * Math.PI * 2;
      // Pequena variacion angular con noise deterministico
      const angleOffset = this.detailNoise(i * 5.0, i * 3.0) * 0.15;
      const finalAngle = angle + angleOffset;

      const x = Math.round(Math.cos(finalAngle) * PILLAR_RING_RADIUS);
      const z = Math.round(Math.sin(finalAngle) * PILLAR_RING_RADIUS);

      // Altura variable entre 20-40 bloques
      const height = 20 + Math.floor((this.detailNoise(i * 7.7, i * 11.3) + 1) * 10);

      pillars.push({ x, z, height });
    }

    this._pillarPositions = pillars;
    return pillars;
  }

  _generatePillars(chunk) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    const pillars = this._getPillarPositions();

    for (const pillar of pillars) {
      // Verificar si el pilar esta cerca de este chunk
      const dx = pillar.x - wx0;
      const dz = pillar.z - wz0;
      if (dx < -2 || dx >= CHUNK_SIZE + 2) continue;
      if (dz < -2 || dz >= CHUNK_SIZE + 2) continue;

      // Calcular base Y del pilar (superficie de la isla en esa posicion)
      const baseY = this._getPillarBaseY(pillar.x, pillar.z);
      if (baseY <= 0) continue;

      // Pilar 3x3 de obsidiana
      for (let py = baseY; py < baseY + pillar.height; py++) {
        for (let pdx = -1; pdx <= 1; pdx++) {
          for (let pdz = -1; pdz <= 1; pdz++) {
            const lx = pillar.x + pdx - wx0;
            const ly = py - wy0;
            const lz = pillar.z + pdz - wz0;
            this._setForce(chunk, lx, ly, lz, BlockId.OBSIDIAN);
          }
        }
      }

      // End rod en la cima (centro)
      const topY = baseY + pillar.height;
      const tlx = pillar.x - wx0;
      const tly = topY - wy0;
      const tlz = pillar.z - wz0;
      this._setForce(chunk, tlx, tly, tlz, BlockId.END_ROD);
    }
  }

  // Encontrar la superficie de la isla principal para un pilar
  _getPillarBaseY(wx, wz) {
    for (let y = MAIN_ISLAND_CENTER_Y + MAIN_ISLAND_RADIUS_Y + 5; y > MAIN_ISLAND_CENTER_Y - MAIN_ISLAND_RADIUS_Y; y--) {
      const block = this._getMainIslandBlock(wx, y, wz);
      if (block !== BlockId.AIR) {
        return y + 1;
      }
    }
    return 0;
  }

  // --- Chorus Plants en Islas Exteriores ---
  _generateChorusPlants(chunk) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // Solo generar en chunks que estan lejos del centro (islas exteriores)
    const chunkCenterX = wx0 + CHUNK_SIZE / 2;
    const chunkCenterZ = wz0 + CHUNK_SIZE / 2;
    const distFromCenter = Math.sqrt(chunkCenterX * chunkCenterX + chunkCenterZ * chunkCenterZ);
    if (distFromCenter < OUTER_ISLAND_MIN_DIST - 20) return;

    // Solo en rango de altura razonable
    if (wy0 + CHUNK_SIZE < 30 || wy0 > 90) return;

    // Revisar posiciones en la grilla del chunk
    for (let lx = 1; lx < CHUNK_SIZE - 1; lx += 3) {
      for (let lz = 1; lz < CHUNK_SIZE - 1; lz += 3) {
        const wx = wx0 + lx;
        const wz = wz0 + lz;

        // Probabilidad de chorus en esta posicion
        const chance = this.chorusNoise(wx * 0.2, wz * 0.2);
        if (chance < 0.55) continue;

        // Buscar superficie de isla exterior debajo
        for (let ly = CHUNK_SIZE - 1; ly >= 1; ly--) {
          const wy = wy0 + ly;
          const below = this._getOuterIslandBlock(wx, wy - 1, wz);
          const here = this._getOuterIslandBlock(wx, wy, wz);

          if (below === BlockId.END_STONE && here === BlockId.AIR) {
            // Plantar chorus: tallo de 2-5 bloques
            const stalkH = 2 + Math.floor(Math.abs(this.chorusNoise(wx * 0.5, wz * 0.5)) * 4);
            for (let dy = 0; dy < stalkH; dy++) {
              const cy = ly + dy;
              if (cy >= 0 && cy < CHUNK_SIZE) {
                chunk.setBlock(lx, cy, lz, BlockId.CHORUS_PLANT);
              }
            }

            // Ramas laterales en la mitad superior
            if (stalkH >= 3) {
              const branchY = ly + Math.floor(stalkH * 0.6);
              if (branchY >= 0 && branchY < CHUNK_SIZE) {
                // Rama en X
                const branchDir = this.chorusNoise(wx * 0.8 + 50, wz * 0.8) > 0 ? 1 : -1;
                const blx = lx + branchDir;
                if (blx >= 0 && blx < CHUNK_SIZE) {
                  chunk.setBlock(blx, branchY, lz, BlockId.CHORUS_PLANT);
                  if (branchY + 1 < CHUNK_SIZE) {
                    chunk.setBlock(blx, branchY + 1, lz, BlockId.CHORUS_PLANT);
                  }
                }

                // Rama en Z (a veces)
                if (this.chorusNoise(wx * 0.6 + 100, wz * 0.6 + 100) > 0.2) {
                  const branchDirZ = this.chorusNoise(wx * 0.9, wz * 0.9 + 50) > 0 ? 1 : -1;
                  const blz = lz + branchDirZ;
                  if (blz >= 0 && blz < CHUNK_SIZE) {
                    chunk.setBlock(lx, branchY, blz, BlockId.CHORUS_PLANT);
                    if (branchY + 1 < CHUNK_SIZE) {
                      chunk.setBlock(lx, branchY + 1, blz, BlockId.CHORUS_PLANT);
                    }
                  }
                }
              }
            }

            break; // Solo un chorus por posicion
          }
        }
      }
    }
  }

  // --- Helpers ---
  _setInChunk(chunk, lx, ly, lz, blockId) {
    if (lx >= 0 && lx < CHUNK_SIZE && ly >= 0 && ly < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
      const current = chunk.getBlock(lx, ly, lz);
      if (current === BlockId.AIR) {
        chunk.setBlock(lx, ly, lz, blockId);
      }
    }
  }

  _setForce(chunk, lx, ly, lz, blockId) {
    if (lx >= 0 && lx < CHUNK_SIZE && ly >= 0 && ly < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
      chunk.setBlock(lx, ly, lz, blockId);
    }
  }
}
